import React, { useCallback, useEffect, useState } from 'react'

import { getTripDetailsForAdmin } from '@/redux/states/admin/trips/getTripDetailsForAdmin/getTripDetailsForAdmin.slice'
import { GetTripDetailsForAdminRequestPayload } from '@/redux/states/admin/trips/getTripDetailsForAdmin/getTripDetailsForAdmin.types'
import { resetUpdateTripDetailsForAdmin } from '@/redux/states/admin/trips/updateTripDetailsForAdmin/updateTripDetailsForAdmin.slice'
import { RootState } from '@/redux/store/store.types'
import date, { dateTimeFormats, formatDuration } from '@/shared/functions/Date/date.functions'
import { useAppDispatch } from '@/shared/hooks/redux.hooks'
import linkStyles from '@/shared/styles/link.styles'
import { Box, Skeleton, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import EditTripDetailsModal from '../EditTripDetailsModal/EditTripDetailsModal.component'
import Button from '@/shared/components/Button/Button.component'
import EmptyState from '@/shared/components/EmptyState/EmptyState.component'
import Link from '@/shared/components/Link/Link.component'
import Table from '@/shared/components/Table/Table.component'

import styles from './TripDetailsCard.styles'
import { TripDetailsCardProps } from './TripDetailsCard.types'

const TripDetailsCard: React.FC<TripDetailsCardProps> = () => {
  const [editModalOpen, setEditModalOpen] = useState(false)
  const dispatch = useAppDispatch()

  const {
    loading: tripDetailsLoading,
    success: tripDetailsSuccess,
    error: tripDetailsError
  } = useSelector((state: RootState) => state.getTripDetailsForAdmin)

  const { success: updateTripSuccess } = useSelector((state: RootState) => state.updateTripDetailsForAdmin)

  const handleEditClick = () => {
    setEditModalOpen(true)
  }

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false)
    // Reset the update state when closing modal
    dispatch(resetUpdateTripDetailsForAdmin())
  }, [dispatch])

  // Refresh trip details when update is successful
  useEffect(() => {
    if (updateTripSuccess && tripDetailsSuccess) {
      const flightItineraryId = tripDetailsSuccess.data.tripDetails.flightItineraryDetails.id.toString()

      const payload: GetTripDetailsForAdminRequestPayload = {
        request: { flightItineraryId },
        onSuccess: () => {
          // Trip details refreshed successfully
        }
      }

      dispatch(getTripDetailsForAdmin(payload))
    }
  }, [updateTripSuccess, tripDetailsSuccess, dispatch])

  if (tripDetailsLoading) {
    return (
      <Box sx={styles.skeletonContainer}>
        <Skeleton variant="text" width="40%" height={40} />
        <Skeleton variant="rectangular" height={200} sx={styles.skeletonItem} />
        <Skeleton variant="text" width="40%" height={40} />
        <Skeleton variant="rectangular" height={200} sx={styles.skeletonItem} />
      </Box>
    )
  } else if (tripDetailsError) {
    return <EmptyState title="Error loading trip details" errorId={tripDetailsError.correlationId} />
  } else if (!tripDetailsSuccess) {
    return <EmptyState title="No trip details found" />
  } else {
    const tripDetails = tripDetailsSuccess?.data.tripDetails
    const isExternalItinerary = tripDetails.flightItineraryDetails.itinerarySource === 'external'

    const renderFlightTable = () => {
      if (!tripDetails.flightBookingDetails?.flights?.length) return null

      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Flight Details
            </Typography>

            {isExternalItinerary && <Button text="Edit" size="small" buttonType="primary" onClick={handleEditClick} />}
          </Box>

          <Table
            columns={[
              { key: 'airline', label: 'Airline' },
              { key: 'flightNumber', label: 'Flight Number' },
              { key: 'departureTime', label: 'Departure Time' },
              { key: 'arrivalTime', label: 'Arrival Time' },
              { key: 'route', label: 'Route' },
              { key: 'duration', label: 'Duration' }
            ]}
            data={tripDetails.flightBookingDetails.flights.map((flight) => ({
              airline: (
                <Box sx={styles.airlineInfo}>
                  <img src={flight.airline.logo} alt={flight.airline.name} style={styles.airlineLogo} />
                  <Typography>{flight.airline.code}</Typography>
                </Box>
              ),
              flightNumber: flight.flightNumber,
              departureTime: (
                <Box>
                  <Typography variant="body2" sx={styles.flightDate}>
                    {date(flight.departureTime).format(dateTimeFormats.date.medium)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {date(flight.departureTime).format(dateTimeFormats.time.short)}
                  </Typography>
                </Box>
              ),
              arrivalTime: (
                <Box>
                  <Typography variant="body2" sx={styles.flightDate}>
                    {date(flight.arrivalTime).format(dateTimeFormats.date.medium)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {date(flight.arrivalTime).format(dateTimeFormats.time.short)}
                  </Typography>
                </Box>
              ),
              route: `${flight.origin.airport.iataCode} → ${flight.destination.airport.iataCode}`,
              duration: formatDuration(flight.duration)
            }))}
          />
        </Box>
      )
    }

    return (
      <Box sx={styles.container}>
        <Box sx={styles.section}>{renderFlightTable()}</Box>

        <Box sx={styles.section}>
          <Table
            title="Booking Details"
            columns={[
              { key: 'key', label: 'Key' },
              { key: 'value', label: 'Data' }
            ]}
            data={
              tripDetails.flightBookingDetails
                ? [
                    {
                      key: 'Confirmation Code',
                      value: tripDetails.flightBookingDetails.confirmationCode || 'N/A'
                    },
                    {
                      key:
                        tripDetails.flightItineraryDetails.itinerarySource === 'external'
                          ? tripDetails.flightBookingDetails.externalDetails?.ticketNumber
                            ? 'Ticket Number'
                            : 'Itinerary Reference Number'
                          : 'Duffel Order ID',
                      value: (
                        <Link href={tripDetails.flightBookingDetails.internalDetails?.duffelOrderUrl} sx={linkStyles}>
                          {tripDetails.flightItineraryDetails.itinerarySource === 'external'
                            ? tripDetails.flightBookingDetails.externalDetails?.ticketNumber
                              ? tripDetails.flightBookingDetails.externalDetails?.ticketNumber
                              : tripDetails.flightBookingDetails.externalDetails?.itineraryReferenceNumber
                            : tripDetails.flightBookingDetails.internalDetails?.duffelOrderId}
                        </Link>
                      )
                    }
                  ]
                : []
            }
          />
        </Box>

        {isExternalItinerary && (
          <EditTripDetailsModal
            open={editModalOpen}
            onClose={handleCloseEditModal}
            tripData={tripDetailsSuccess.data}
          />
        )}
      </Box>
    )
  }
}

export default TripDetailsCard
